/**
 * Sidebar — Figma `Shell/Sidebar` (node 12:908), 240px.
 *
 * Measured: `bg/sidebar` with a right `border/default`; a 56px UserBlock
 * (32px `accent/violet` avatar, `Body/14-Med` name, a ▾); the nav at px-12/py-8
 * with 4px gaps, `SectionHeader` in `TableHeader/11` uppercase `text/muted` with
 * pt-14/pb-4/pl-10, items 36px tall at px-10 `radius/sm` with a 10px icon gap and
 * `Body/14-Med` labels; the active item takes `accent/indigo-soft` fill with
 * `accent/indigo` text; a 56px footer with the 28px mark and the wordmark
 * in `Body/13` `text/secondary`.
 *
 * RENAMED 2026-08-12: the mark is the owner-supplied G1 and the wordmark reads
 * "Global One". Figma still says "GS"/"Global Softwares" — that is the design
 * being older than the rename, not a build deviation.
 *
 * RULING 3.5 (2026-08-20): two of the measured inks above deliberately diverge
 * from Figma, because they measured under WCAG AA on the rail they actually sit
 * on: the section heading wears `text/secondary` (not `text/muted`, 2.63:1
 * light) and the active label wears the authored `indigo-strong` (not
 * `accent/indigo`, 3.72:1 on its own tint in both modes). Ratios and the
 * owner's ruling are pinned in `lever7-contrast.test.ts` §7; the fills are
 * untouched.
 *
 * ===========================================================================
 * THE ICONS ARE OURS. THE MAPPING IS THE DELIVERABLE.
 * ===========================================================================
 * `Shell/Sidebar` draws every icon as an EMPTY 16×16 outlined square — the
 * inherited design defect the P2-T0 gate confirmed (V4). Each label is therefore
 * mapped to a lucide glyph. The MAP lives in `domain/nav.ts` as data (`iconName`);
 * this file owns only the string→component table, so the mapping can be reviewed,
 * reported and changed without touching a component.
 *
 * The active item is matched with `end` on the dashboard — `/countries/IN`
 * must not stay highlighted while the user is inside `/countries/IN/inquiries`,
 * but `/countries/IN/inquiries/42` must keep Inquiries lit — and, since v21
 * Unit C, on any row another ROW's path nests under (derived where the rows
 * render): Sign Offs must not stay lit on `signoffs/acceptance-chase`, which
 * is a sibling destination with a row of its own, not a detail page.
 *
 * ===========================================================================
 * PHASE 2.5a · LEVER L1 — THE BRAND BLOCK IS NEW, AND IT IS THE ONLY RED
 * ===========================================================================
 * A 56px identity row is added above the UserBlock: monogram tile, wordmark,
 * active-country pill. Nothing else in the file moved — the UserBlock, the nav
 * and the footer are byte-identical to Phase 2. See `BrandBlock` below for the
 * red-reservation rule and the monogram size floor, both of which are enforced
 * by `sidebar-brand.test.tsx` rather than by this comment.
 *
 * ===========================================================================
 * D2 — THE SIDEBAR YIELDS BELOW 1440px. IT USED TO YIELD AT NO WIDTH AT ALL
 * ===========================================================================
 * `docs/WEB-DESIGN-DIRECTION.md` §10 D2 (accepted; see that file's repo note):
 * "Collapse the sidebar automatically below 1440px. 240 → 64 returns 176px …
 * `adaptive-navigation`. Mitigation: it is automatic and width-driven, never a
 * user toggle, and labels return the moment the window widens."
 *
 * WHAT IT WAS BEFORE, MEASURED rather than described: `w-sidebar shrink-0` gave
 * this element a bounding width of exactly **240px at every viewport width
 * tested — 375, 431, 640, 768, 1024, 1280, 1440 and 1920**. At 431px that left
 * `main` 191px, and the shell's own minimum was 470px (240 here + 230 of top
 * bar), so on a narrow window the WHOLE APP — sidebar included — scrolled
 * sideways while the card contents were clipped where no scrollbar reached
 * them. That is the defect this change exists to remove.
 *
 * HOW IT IS BUILT, and why there is no toggle: every adaptive class is a
 * `max-shell:` variant off `--breakpoint-shell` (`styles/app.css`), so the
 * ≥1440 rendering is the OLD declarations untouched rather than a second set of
 * rules that has to be kept in agreement with them. D2 says "never a user
 * toggle" and this is how that is guaranteed: there is no state to toggle. It
 * also means no hamburger appears at a width where the nav is already visible —
 * the nav is visible at EVERY width, in one of two dresses.
 *
 * WHAT THE RAIL KEEPS: the nav landmark, every destination, every destination's
 * accessible NAME (`sr-only`, never `hidden`), the active-item treatment, the
 * brand tile and the footer mark. What it drops VISUALLY: the labels, the
 * counts, the section headings, the wordmark and the country pill — of which
 * only the country pill and the wordmark leave the accessibility tree, and both
 * are said again in the top bar and the footer respectively.
 */

import type { ReactNode } from 'react';
/* Value imports, added for the collapsible sections below. `ReactNode` stays a
 * TYPE import: this file has never imported the React namespace as a value and
 * does not start now — `React.JSX.Element` in the signatures resolves through the
 * global UMD declaration, exactly as it did before. */
import { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Building2,
  CircleCheck,
  ClipboardList,
  Columns3,
  Download,
  FileCheck,
  FileSignature,
  FileText,
  FlaskConical,
  Inbox,
  KeyRound,
  Landmark,
  LayoutDashboard,
  ListChecks,
  PackageCheck,
  Percent,
  Receipt,
  Rocket,
  Server,
  ShieldCheck,
  UserCog,
  Users,
  Package,
  Tags,
  CalendarClock,
  CalendarOff,
  Radar,
  Mails,
  // v17 field tracking — the Field section's two rows + the Admin settings row.
  CalendarCheck,
  MapPin,
  LocateFixed,
  // v19 DASHBOARDS · W2 — the Group dashboard's glyph (every country, one view).
  Globe,
  // ── v19 DASHBOARDS · W1 — the Team section's two rows (BEGIN W1) ──
  Gauge,
  Activity,
  // ── v19 DASHBOARDS · W1 (END W1) ──
  /* The fold affordance. ONE glyph, rotated -90° when shut, rather than a
   * chevron-down/chevron-right pair: two imports for one state is how the two
   * drift apart. `aria-hidden` — the state lives on the button. */
  ChevronDown,
  // Vocabulary admin — platforms, the per-country availability switch, stacks.
  MonitorSmartphone,
  ToggleLeft,
  Layers,
  // v21 · UNIT C — the Acceptance section's one row (a document being looked for).
  FileSearch,
  CircleAlert,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  groupNavForDisplay,
  navHref,
  // v21 · UNIT C — the authored Acceptance section (the chase list's row).
  visibleAcceptanceNavSection,
  visiblePaymentsNavSection,
  visibleAdminNavSection,
  visibleFieldNavSection,
  visibleNavSections,
  visibleRenewalsNavSection,
  visibleSalesNavSection,
  // ── v19 DASHBOARDS · W1 (BEGIN W1) ──
  visibleTeamNavSection,
  // ── v19 DASHBOARDS · W1 (END W1) ──
} from '../../domain/nav';
import type { NavAuthority, NavIconName } from '../../domain/nav';
import type { CountryCode } from '../../domain/countries';
import { CountryBadge } from '../ui';
import { G1Glyph, G1Mark } from '../EntryBackdrop';
import { cn } from '../../lib/cn';

/**
 * THE ICON MAPPING (label → lucide export), reported in PHASE2-REPORT so a
 * designer can accept or replace it row by row:
 *
 *   Dashboard          → LayoutDashboard   (a panel of tiles: the widget grid)
 *   Inquiries          → Inbox             (arriving work, not yet triaged)
 *   CRFs               → FileText          (the CRF document itself)
 *   Work Board         → Columns3          (a kanban of columns)
 *   DL Files           → Download          (downloadable deliverables)
 *   Testing            → FlaskConical      (the test round)
 *   Deployment         → Rocket            (the act of shipping)
 *   Deployed Projects  → Server            (what is live and installed)
 *   Sign Offs          → FileCheck         (a signed document)
 *   Project Received   → ClipboardList     (an intake report)
 *   Complete Work      → CircleCheck       (finished work)
 *   Billing            → Receipt           (invoices and advances)
 *   Parties            → Building2         (customer companies)
 *
 * Phase 2.5b — the AUTHORED Admin section (no Figma sidebar row exists):
 *   Users              → Users             (the people themselves)
 *   Agent keys         → KeyRound          (a credential)
 *   Network rules      → ShieldCheck      (premises access control, D7)
 *   Go-live checklist  → ListChecks        (a list of things to tick off)
 *
 * Phase 3a · U5 — the AUTHORED Sales section (no Figma sidebar row exists):
 *   Quotes             → FileSignature     (a priced document somebody signs off)
 *   Sales orders       → PackageCheck      (a committed order, checked off)
 *
 * Phase 5 · U5.5 / U2 — the AUTHORED Renewals section (no Figma sidebar row exists):
 *   Renewals           → CalendarClock     (a date something ends on)
 *   Reminder drafts    → Mails             (a stack of letters nobody has sent)
 */
const NAV_ICONS: Record<NavIconName, LucideIcon> = {
  Tags,
  Package,
  LayoutDashboard,
  Inbox,
  FileText,
  Columns3,
  Download,
  FlaskConical,
  Rocket,
  Server,
  FileCheck,
  ClipboardList,
  CircleCheck,
  Receipt,
  Building2,
  Users,
  KeyRound,
  ShieldCheck,
  UserCog,
  ListChecks,
  FileSignature,
  PackageCheck,
  CalendarClock,
  Mails,
  // Tax groups (design U5) — the screen manages percentages.
  Percent,
  // Country settings (India go-live, A3) — the registered entity, not a customer company.
  Landmark,
  // v17 field tracking — a day checked off, a place gone to, and the capture switchboard.
  CalendarCheck,
  CalendarOff,
  Radar,
  MapPin,
  LocateFixed,
  // v19 DASHBOARDS · W2 — Group dashboard (every active country, one view).
  Globe,
  // ── v19 DASHBOARDS · W1 — Team (load) and Who does what (activity). (BEGIN W1) ──
  Gauge,
  Activity,
  // ── v19 DASHBOARDS · W1 (END W1) ──
  // Vocabulary admin — Platforms / Platform availability / Stacks (nav.ts records why).
  MonitorSmartphone,
  ToggleLeft,
  Layers,
  // v21 · UNIT C — Acceptance chase (a document being looked for; see nav.ts's union note).
  FileSearch,
  CircleAlert,
};

/* ===========================================================================
 * THE LOGO SWAP SLOT — FILLED, 2026-08-12
 * ===========================================================================
 * The slot did its job. It was `null` while the real vector was outside the
 * repo; the owner supplied it on 2026-08-12 as the G1 monogram for the app's
 * new internal name, **Global One**, and it lands here exactly as the slot's
 * instructions said it would — inline, not an <img> and not an import, so the
 * mark stays in the bundle with no extra request and takes `currentColor`.
 *
 * ⚠ `currentColor` IS NOT A STYLE CHOICE. `sidebar-brand.test.tsx` asserts the
 * rendered sidebar "carries no literal colour — the tile is a token, not a
 * hex". An inline `fill="#ed1c24"` would fail that assertion ON ITS OWN TERMS,
 * and the correct response would not be to relax the test. The tile already
 * sets `text-fg-inverse`, so `currentColor` paints the mark white on the red
 * and the colour keeps coming from exactly one place.
 *
 * ⚠ THE RED TILE STAYS, AND THE SLOT'S OWN NOTE ANTICIPATED THE QUESTION: "a
 * logo that carries its own colour usually wants `bg-transparent`". The
 * owner's artwork IS red-on-white, so inverting the tile to a white plate with
 * a red mark is a defensible reading of it. It is NOT taken here, for a reason
 * outside this file: `bg-brand-tile` is the app's one reserved red and
 * `sidebar-brand.test.tsx` counts it, so flipping the tile would spend that
 * architecture on a change nobody asked for. Red plate + white mark is also
 * what `assets/brand/brand-mark-1024.png` and the launcher icons already are,
 * so the web and the handset agree. Filed as an owner decision in the report;
 * reversing it is a one-class edit in `BrandBlock` plus the token in the mark.
 *
 * The drawing itself is `G1Glyph`, which the login screen's mark also uses —
 * ONE copy of the path in this app, not two. Its source is
 * `apps/mobile/assets/brand/g1-mark.svg` on its 392 x 300 authoring viewBox,
 * the SAME path the Flutter client transcribes in
 * `lib/src/design/brand_mark.dart`. If it is ever re-cut, re-cut the SVG and
 * copy from it, rather than nudging one of the three.
 * =========================================================================== */
const BRAND_LOGO: ReactNode = <G1Glyph className="w-7" />;

/**
 * BrandBlock — Phase 2.5a lever L1. AUTHORED: Figma's `Shell/Sidebar` (12:908)
 * has no brand row at the top; it puts the 28px GS mark in the FOOTER only.
 *
 * ===========================================================================
 * RED IS RESERVED. THIS TILE IS THE ONLY PLACE IT APPEARS IN THE APP CHROME
 * ===========================================================================
 * `bg-brand-tile` (#ed1c24) is the identity red and it means "this is the app"
 * — nothing else. Danger still means danger and still uses `danger`/`brand`. If
 * a second `bg-brand-tile` ever appears in the shell, the colour stops being a
 * signature and becomes decoration; `sidebar-brand.test.tsx` counts them so that
 * cannot happen quietly.
 *
 * ===========================================================================
 * THE MARK'S SIZE — WHY THE OLD 18.66px FLOOR NO LONGER APPLIES, AND WHAT DOES
 * ===========================================================================
 * ⚠ READ THIS BEFORE CITING 4.38:1 AT ANYTHING. The constraint that used to
 * live here was a TEXT constraint. #ffffff on #ed1c24 measures 4.38:1, which
 * fails WCAG's 4.5:1 normal-text bar and clears the 3:1 large-text bar, so the
 * "GS" monogram was pinned to ≥18.66px/700 and the test asserted `text-h2
 * font-bold` to stop a later contributor shrinking it into a silent failure.
 *
 * The tile no longer contains text. It contains a LOGO, which WCAG 1.4.11
 * scores as a graphical object against a 3:1 bar — and 4.38:1 clears that at
 * ANY size. The type-size mitigation has nothing left to mitigate.
 *
 * What replaces it is a LEGIBILITY floor, measured on this specific drawing
 * rather than inherited from a contrast table: rasterised, the G1 keeps its
 * counter open and the 1 keeps its flag down to **16px of mark height**, and
 * below that the counter fills in and the 1 reads as an "I". The mark is drawn
 * at `w-7` (28px) inside a 40px tile, and on its 392×300 viewBox that is
 * 21.4px tall — 5.4px of headroom.
 *
 * DO NOT shrink it below `w-6` (24px → 18.4px tall). The test asserts the width
 * class for that reason, and the reason is now sharpness, not contrast.
 */
function BrandBlock({ countryCode }: { countryCode: CountryCode }): React.JSX.Element {
  return (
    <div className="flex h-topbar shrink-0 items-center gap-2.5 border-b border-line px-4 max-shell:justify-center max-shell:px-2">
      <span
        aria-hidden="true"
        className="inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-brand-tile text-fg-inverse"
      >
        {BRAND_LOGO ?? (
          // The fallback the swap slot shipped with, kept as a fallback: if
          // `BRAND_LOGO` is ever emptied, this renders rather than nothing.
          // 20px/700 — the old TEXT floor still governs this branch, because
          // this branch really is text. See the header for why the mark is not.
          <span className="text-h2 font-bold leading-none">G1</span>
        )}
      </span>

      {/* D2 · the rail keeps the TILE and drops this column. Not `sr-only`:
       * both things in it are said again, and better, elsewhere in the chrome —
       * the wordmark by the sidebar footer (which IS kept for AT below), the
       * active country by the top bar's `CountrySwitcher`, which is a real
       * control rather than a badge. Announcing them twice at rail widths would
       * be adding noise to buy nothing. */}
      <span className="flex min-w-0 flex-col items-start gap-0.5 max-shell:hidden">
        <span className="max-w-full truncate text-body13 font-medium text-fg">
          Global One
        </span>
        {/* The active country, as a pill.
         *
         * PHASE 3a — THIS IS NOW `CountryBadge`, AND IT IS THE SAME PILL. The 2.5a
         * decision recorded here is the one the shared badge was built from, so
         * adopting it changes the reasoning's home, not the reasoning:
         *
         * `text-fg-secondary` on the sidebar rather than the `accent/indigo-soft`
         * chip the topbar switcher uses: that pair measures 3.72:1 (#5b6cfa on
         * #eef0fe) and this row is not the place to add a sixth instance of it.
         * Bordered measures 4.64:1 light / 5.85:1 dark — see the ratio table in
         * the report.
         *
         * The CODE is the visible token because that is how this app already
         * writes a country (`CountrySwitcher`); the NAME carries the meaning for
         * anyone who does not read ISO codes, so it goes in the label — which is
         * why `label` is passed: this pill says "Active country", not "Country",
         * and `sidebar-brand.test.tsx` asserts that exact string.
         *
         * LEVER 7 — THE BORDER WAS THERE AND YOU COULD NOT SEE IT. The pair above
         * was measured on the TEXT and the text is fine (4.64 light / 5.85 dark).
         * The border was not measured, and `border/default` against the sidebar it
         * is drawn on is 1.22:1 in light and 1.21:1 in dark — so the "pill" read as
         * bare mono text in both modes and the shape carried nothing.
         *
         * `border-fg-muted` is `text/muted` #9aa0aa, mode-invariant, which is why
         * one class fixes both halves: 2.63:1 light, 6.26:1 dark. Neither number is
         * a compliance threshold — the pill is `role="img"` with its meaning in the
         * label and its code in text, so WCAG 1.4.11's decoration exemption applies
         * either way — they are the difference between a pill and no pill.
         *
         * TWO THINGS THE SHARED COMPONENT ADDS, both deliberate: a fixed 20px
         * height (`h-country-badge`, up from the ~18px the line box gave) so every
         * badge in the app is one size, and an opaque `bg-surface` fill — a no-op
         * here, because `bg/sidebar` and `bg/surface` are the same value in both
         * modes, and load-bearing on the table rows this pill's treatment now also
         * has to survive. */}
        <CountryBadge code={countryCode} label="Active country" />
      </span>
    </div>
  );
}

/*
 * COLLAPSIBLE SECTIONS — presentation only.
 *
 * MEASURED 2026-08-25: a group admin sees 32 rows under 9 headers; `Admin` is 10
 * of them. Merging the sections was REFUSED: `nav.test.ts` pins `NAV_SECTIONS` as
 * "all thirteen destinations the Figma sidebar lists" and two further cases assert
 * later registries are "NOT folded into NAV_SECTIONS — the thirteen are the Figma
 * record". Regrouping is a design-record change and the owner's to make.
 *
 * So no membership, order or gating moves here. Three rules:
 *  1. `Admin` starts folded; the rest start open.
 *  2. The section holding the CURRENT ROUTE is always open — folding the row you
 *     are standing on reads as the app losing your place.
 *  3. ⚠ Below `--breakpoint-shell` (1440px) nothing folds. That is the icon rail,
 *     where the heading is `sr-only` and there is no visible control to unfold
 *     with — a fold there would remove destinations with no way back.
 */
const COLLAPSE_STORAGE_KEY = 'crf.nav.collapsed';
const SHELL_BREAKPOINT_PX = 1440;
const DEFAULT_COLLAPSED: readonly string[] = ['Admin'];

/** True at or above the shell breakpoint — i.e. NOT the icon rail. */
function useIsShellWidth(): boolean {
  const query = `(min-width: ${String(SHELL_BREAKPOINT_PX)}px)`;
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    /* Same guard as `theme.tsx`: jsdom has no `matchMedia`. Defaulting TRUE keeps
     * the full-width layout every existing test asserts against. */
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };
    list.addEventListener('change', listener);
    return () => list.removeEventListener('change', listener);
  }, [query]);
  return matches;
}

/** The folded section titles, persisted per browser. */
function useCollapsedSections(): {
  collapsed: ReadonlySet<string>;
  toggle: (title: string) => void;
} {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => {
    /* Blocked site data, a private window, or a thumbnailer that throws on the
     * accessor must all still get a working sidebar. The fallback is the DEFAULT
     * list, not an empty set — an empty set would silently unfold Admin. */
    try {
      const raw = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
      if (raw === null) return new Set(DEFAULT_COLLAPSED);
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set(DEFAULT_COLLAPSED);
      return new Set(parsed.filter((entry): entry is string => typeof entry === 'string'));
    } catch {
      return new Set(DEFAULT_COLLAPSED);
    }
  });

  const toggle = useCallback((title: string): void => {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        /* The fold still works for this page view when it cannot be remembered. */
      }
      return next;
    });
  }, []);

  return { collapsed, toggle };
}

export interface SidebarProps {
  /** Narrowed from `string`: the brand block resolves the country's real name. */
  countryCode: CountryCode;
  authority: NavAuthority;
  /** Rendered in the 56px UserBlock — the identity menu trigger. */
  userBlock: React.ReactNode;
  /** Per-item trailing counts, keyed by nav item id (Figma shows "28" on Inquiries). */
  counts?: Partial<Record<string, number>>;
}

export function Sidebar({
  countryCode,
  authority,
  userBlock,
  counts,
}: SidebarProps): React.JSX.Element {
  /*
   * Figma's thirteen destinations first, then the two AUTHORED sections.
   *
   * SALES BEFORE ADMIN, deliberately: Sales is daily work for the pilot teams and
   * Admin is occasional configuration, so the ordering is by how often somebody
   * reaches for it. Both are `null` for a caller with no permission in them, so a
   * section header never renders over an empty list.
   */
  // v21 · UNIT C — the Acceptance section (the chase list).
  const acceptanceSection = visibleAcceptanceNavSection(authority);
  const salesSection = visibleSalesNavSection(authority);
  // v24 Phase B — the Payments section (the backfill exceptions register).
  const paymentsSection = visiblePaymentsNavSection(authority);
  const renewalsSection = visibleRenewalsNavSection(authority);
  const fieldSection = visibleFieldNavSection(authority);
  // ── v19 DASHBOARDS · W1 (BEGIN W1) ──
  const teamSection = visibleTeamNavSection(authority);
  // ── v19 DASHBOARDS · W1 (END W1) ──
  const adminSection = visibleAdminNavSection(authority);
  const registrySections = [
    ...visibleNavSections(authority),
    /*
     * v21 · UNIT C — Acceptance sits FIRST after the Figma record: its subject is
     * the deployment pipeline's paperwork, so it belongs as close as an authored
     * section can get to the Deployment section (which ends with Sign Offs, the
     * chase's sibling register) without touching the pinned thirteen.
     */
    ...(acceptanceSection === null ? [] : [acceptanceSection]),
    ...(salesSection === null ? [] : [salesSection]),
    // Payments sits after Sales: Accounts' follow-on to what was sold, and the
    // reading order of the sidebar should match the order of the work.
    ...(paymentsSection === null ? [] : [paymentsSection]),
    // Renewals sits after Sales: it is a follow-on to something already sold, and the
    // reading order of the sidebar should match the order of the work.
    ...(renewalsSection === null ? [] : [renewalsSection]),
    // Field (v17) sits after the commercial sections and before Admin: daily
    // operational reading, but about the team rather than the pipeline.
    ...(fieldSection === null ? [] : [fieldSection]),
    // ── v19 DASHBOARDS · W1 (BEGIN W1) ── Team (v19) sits after Field and before Admin:
    // management reading about the people doing the work, not a configuration screen.
    ...(teamSection === null ? [] : [teamSection]),
    // ── v19 DASHBOARDS · W1 (END W1) ──
    ...(adminSection === null ? [] : [adminSection]),
  ];

  /*
   * THE DISPLAY GROUPING (see `NAV_DISPLAY_GROUPS`). The registry above is the
   * design record and is NOT regrouped; this re-stacks what it produced into
   * six headings instead of ten, and it runs AFTER every permission filter, so
   * it can narrow nothing and widen nothing. Measured: 36 rows / 10 headings
   * before, 36 rows / 6 headings after — no destination gained or lost.
   */
  const sections = groupNavForDisplay(registrySections);

  /*
   * FOLD STATE. `isShellWidth` is the rail guard (rule 3): below 1440 the heading
   * is `sr-only`, so nothing folds and `foldable` is false for every section.
   *
   * `activeSectionTitle` is rule 2 — the section holding the current route stays
   * open whatever storage says. It is matched on the RESOLVED href rather than on
   * the item path, because `navHref` is what the NavLink actually points at, and
   * matching anything else would drift from the highlight the user can see.
   */
  const { collapsed, toggle } = useCollapsedSections();
  const isShellWidth = useIsShellWidth();
  const { pathname } = useLocation();
  const activeSectionTitle =
    sections.find((section) =>
      section.items.some((item) => {
        const href = navHref(countryCode, item);
        return item.path === '' ? pathname === href : pathname.startsWith(href);
      }),
    )?.title ?? null;

  /*
   * v21 · UNIT C — WHICH ROWS TAKE `end`, AND WHY IT IS DERIVED, NOT HARDCODED.
   *
   * `NavLink`'s default match lights a row for every URL under its path. That
   * is RIGHT for a detail page with no row of its own — `inquiries/42` must
   * keep Inquiries lit — and WRONG the moment another ROW's path nests under
   * this one: the Acceptance chase lives at `signoffs/acceptance-chase`, and
   * without `end` the Sign Offs row lit beside it (measured on the live shell),
   * claiming the reader was on a screen they were not. The distinction is
   * exactly "does another DESTINATION extend this path", so that is the
   * computed rule rather than a per-row flag somebody forgets to set when the
   * next nested destination ships.
   */
  const allItemPaths = sections.flatMap((section) => section.items.map((entry) => entry.path));

  return (
    <nav
      aria-label="Main"
      /*
       * THE CHROME SEAM IN DARK — verdict DK-A. ONE CLASS, and it is the only
       * line of this file the "top bar recedes" change touches.
       *
       * In LIGHT the seam is a FILL: the top bar took the canvas (#eef1f6) and
       * sits 1.13:1 against this white rail. In DARK that move buys only 1.09
       * (#161616 against #1f1f1f) and there is nowhere left to go — L(#1f1f1f)
       * is 0.0137, so PURE BLACK against this rail is 1.27:1 and the entire
       * remaining prize is 0.18. Both ways of chasing it were measured and both
       * lose: a darker authored canvas buys +0.08 and reverses the pinned owner
       * ruling that the dark canvas IS the shipped bg/page, and lifting the bar
       * to #3a3a3a drops the focus ring to 2.69 (under 1.4.11) and the
       * breadcrumb trail to 4.32 (under AA) on a value already spoken for as
       * `--crf-skeleton-base`.
       *
       * So dark's seam is carried by the RULE instead of the fill:
       * `text/inverse` at 24% over `bg/sidebar` — a composite of two EXISTING
       * tokens (D4 method (a), the mechanism `lever7-contrast.test.ts` already
       * models for `dark:bg-indigo/20`). It authors no token and adds no dark
       * override. MEASURED IN A REAL BROWSER, not derived: Chromium paints
       * #555555, which is 2.21:1 against this rail and 2.42:1 against the
       * receded bar, up from border/default's 1.21.
       *
       * `border-r-` and not `border-`: only the right edge is the seam, and the
       * narrower spelling keeps this away from the rail's other hairlines.
       * NOTHING ELSE IN THE SIDEBAR CHANGES — not its ground, not its light
       * hairline, and not the two AA findings filed against its section
       * headings and its active nav label, which are the owner's and not this
       * change's.
       */
      className="flex h-dvh w-sidebar shrink-0 flex-col border-r border-line bg-sidebar max-shell:w-sidebar-rail dark:border-r-fg-inverse/24"
    >
      <BrandBlock countryCode={countryCode} />

      <div className="flex h-topbar shrink-0 items-center justify-between border-b border-line pl-4 pr-3.5 max-shell:justify-center max-shell:px-2">
        {userBlock}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-2 max-shell:px-2">
        {sections.map((section, index) => {
          /* Rule 2 and rule 3 are BOTH expressed here rather than in the state:
           * a section you are standing in, or a rail, is not foldable at all —
           * which is why a folded-in-storage section still renders its rows when
           * you navigate into it, and why the rail is untouched. */
          /* An empty title is the leading UNHEADED group (Dashboard). There is no
           * heading to click, so it can never fold — and rendering a nameless
           * button would be an unlabelled control. */
          const foldable =
            isShellWidth && section.title !== '' && section.title !== activeSectionTitle;
          const isFolded = foldable && collapsed.has(section.title);
          return (
          <div
            key={section.title}
            className={cn(
              'flex flex-col gap-1',
              /* The section HEADING is the rail's one real loss: "PIPELINE"
               * cannot be set in 47px, and shrinking it is how a label becomes
               * an unreadable smudge rather than a label. It goes `sr-only`
               * below the breakpoint — still announced, still in the outline —
               * and a hairline in the EXISTING `border-line` token carries the
               * grouping visually, which is the only job the heading was doing
               * for a sighted reader at that size. First section takes no rule:
               * there is nothing above it to separate it from. */
              index === 0 ? '' : 'max-shell:mt-1 max-shell:border-t max-shell:border-line max-shell:pt-1',
            )}
          >
            {/* RULING 3.5 (owner, verbatim: "fix both. An accessibility rule
             * enforced in one mode and not the other is not a rule.") — this
             * heading wore `text/muted`, which MEASURED 2.63:1 on the white
             * rail and 6.26:1 on the dark one: enforced in one mode only. At
             * 11px/500 uppercase it is NOT large text (SC 1.4.3 needs
             * ≥18.66px/700 or ≥24px), so 4.5:1 is the bar. `text-fg-secondary`
             * is §6.4's target for every muted site and measures 4.64 light /
             * 5.85 dark here — one token, both modes over the bar. Figma says
             * `text/muted` (node 12:908); the divergence is deliberate and
             * pinned in `lever7-contrast.test.ts` §7. */}
            {/* THE HEADING IS NOW THE CONTROL, and it stays a heading when it is
              * not one. `foldable` is false on the rail and false for the section
              * you are standing in, and in both cases this renders the ORIGINAL
              * `<p>` — same classes, same ink, same ratios, so §7's pinned 4.64 /
              * 5.85 measurements are untouched on every path that still draws it.
              *
              * When it IS foldable it becomes a `<button>` carrying the same
              * classes plus `aria-expanded`, which is what makes the fold audible
              * rather than merely visible. The chevron is `aria-hidden`: the state
              * is already on the button and saying it twice is noise. */}
            {foldable ? (
              <button
                type="button"
                onClick={() => {
                  toggle(section.title);
                }}
                aria-expanded={!isFolded}
                className="flex w-full items-center justify-between rounded-sm pb-1 pl-2.5 pr-1.5 pt-3.5 text-th uppercase text-fg-secondary hover:bg-subtle"
              >
                {section.title}
                <ChevronDown
                  aria-hidden="true"
                  className={cn('size-3.5 shrink-0 transition-transform', isFolded && '-rotate-90')}
                  strokeWidth={2}
                />
              </button>
            ) : section.title === '' ? (
              /* THE UNHEADED GROUP renders NO element at all, not an empty one.
               * An empty `<p>` is a heading a screen reader announces as nothing,
               * and the padding it carries would open a gap above Dashboard that
               * reads as a missing label rather than a deliberate absence. */
              null
            ) : (
              <p className="pb-1 pl-2.5 pt-3.5 text-th uppercase text-fg-secondary max-shell:sr-only">
                {section.title}
              </p>
            )}
            {/* FOLDED MEANS NOT RENDERED, not `hidden`. A `display:none` row is
              * still in the DOM and still found by `getByRole('link')`, which
              * would let a suite assert a destination is reachable when a sighted
              * user cannot reach it — the exact class of lie the `sr-only`-not-
              * `hidden` note above exists to avoid, in the opposite direction. */}
            {isFolded
              ? null
              : section.items.map((item) => {
              const Icon = NAV_ICONS[item.iconName];
              const count = counts?.[item.id];
              return (
                <NavLink
                  key={item.id}
                  to={navHref(countryCode, item)}
                  end={
                    item.path === '' ||
                    allItemPaths.some((path) => path.startsWith(`${item.path}/`))
                  }
                  /*
                   * D2's RECORDED OBJECTION, answered as cheaply as it can be.
                   * The ruling collapses to icons and says so with its own
                   * caveat attached: "the Figma sidebar draws every icon as an
                   * empty square and the lucide mapping is ours, so an icon-only
                   * rail makes an acknowledged guess load-bearing" —
                   * `Testing` / `Deployment` / `Deployed Projects` are three
                   * destinations only their labels tell apart.
                   *
                   * The native `title` is the smallest possible mitigation: no
                   * component, no token, no colour, no state. It does NOT touch
                   * the accessible name — under accname a `title` is a fallback
                   * used only when an element has no other name source, and this
                   * link always has its label text — so it is a mouse tooltip
                   * and nothing else. It is the one thing here that is BEYOND
                   * D2's letter, and it is here because D2's own objection asked
                   * for it.
                   */
                  title={item.label}
                  className={({ isActive }) =>
                    cn(
                      'flex h-9 items-center justify-between rounded-sm px-2.5 text-body font-medium',
                      'max-shell:justify-center max-shell:px-0',
                      /* RULING 3.5 — `text-indigo` on this fill MEASURED
                       * 3.72:1, in BOTH modes (both halves mode-invariant).
                       * The ink moves one step down its own family to the
                       * authored `indigo-strong` #4f5ed6 (WT1/D1a): 4.74:1 on
                       * `indigo-soft`, both modes in one class, icon included
                       * via currentColor (1.4.11 needs 3). THE FILL DID NOT
                       * MOVE — 4.74 clears the bar without touching it, so
                       * §5.8's separate finding about this fill in dark
                       * (14.54:1 near-white block) stays a finding, not a
                       * silent scope expansion. Pinned red-first in
                       * `lever7-contrast.test.ts` §7. */
                      isActive
                        ? 'bg-indigo-soft text-indigo-strong'
                        : 'text-fg hover:bg-subtle',
                    )
                  }
                >
                  <span className="flex items-center gap-2.5 max-shell:gap-0">
                    <Icon aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.5} />
                    {/* `sr-only`, NOT `hidden`. The label is what names this
                     * link to a screen reader and to every `getByRole('link',
                     * { name })` in the suite; `display:none` would delete the
                     * destination's name from the accessibility tree and leave
                     * an unlabelled icon behind. */}
                    <span className="max-shell:sr-only">{item.label}</span>
                  </span>
                  {count === undefined ? null : (
                    <span className="tnum text-label font-normal text-fg-secondary max-shell:sr-only">
                      {count}
                    </span>
                  )}
                </NavLink>
              );
                })}
          </div>
          );
        })}
      </div>

      <div className="flex h-topbar shrink-0 items-center gap-2 border-t border-line px-4 max-shell:justify-center max-shell:px-2">
        <G1Mark size={28} />
        <span className="text-body13 text-fg-secondary max-shell:sr-only">Global One</span>
      </div>
    </nav>
  );
}
